'use client';

import Link from 'next/link';
import { ArrowRight } from 'lucide-react';

/**
 * The Free Tools menu: a purpose-built showcase (desktop mega menu + phone menu). Titles, descriptions and hrefs come from
 * content/data/navigation.json via `columns`; only the mini visuals and the badges are hard-coded here. Every tool and guide
 * renders exactly once. The visuals are illustrative shapes with no figures. Motion is transform/opacity only.
 */
type Item = { label: string; href: string; description?: string };
type Column = { title: string; items: Item[] };

const GRADER_HREF = '/mortgage-website-grader';
const DOWNLOADED_CLAIM_HREF = '/guide/mortgage-lead-generation-apocalypse'; // the "25,000+" claim lives only on this guide
const COVER_TINTS = ['lpft-t1', 'lpft-t2', 'lpft-t3', 'lpft-t4'];

function GraderArt() {
  return (
    <svg className="lpft-art lpft-grader" viewBox="0 0 150 96" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="lpftRing" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff34a7" />
          <stop offset=".5" stopColor="#7c5cff" />
          <stop offset="1" stopColor="#22e4ff" />
        </linearGradient>
      </defs>
      <g transform="translate(48 48)">
        <circle r="36" fill="none" className="lpft-track" strokeWidth="9" />
        <g className="lpft-sweep">
          <circle r="36" fill="none" stroke="url(#lpftRing)" strokeWidth="9" strokeLinecap="round" strokeDasharray="158 226" transform="rotate(-90)" />
        </g>
        <circle r="20" className="lpft-core" />
      </g>
      <g className="lpft-bars">
        <rect x="100" y="24" width="44" height="7" rx="3.5" className="lpft-track-fill" />
        <rect x="100" y="24" width="34" height="7" rx="3.5" fill="url(#lpftRing)" className="lpft-bar" style={{ ['--i' as string]: 0 }} />
        <rect x="100" y="44" width="44" height="7" rx="3.5" className="lpft-track-fill" />
        <rect x="100" y="44" width="25" height="7" rx="3.5" fill="url(#lpftRing)" className="lpft-bar" style={{ ['--i' as string]: 1 }} />
        <rect x="100" y="64" width="44" height="7" rx="3.5" className="lpft-track-fill" />
        <rect x="100" y="64" width="30" height="7" rx="3.5" fill="url(#lpftRing)" className="lpft-bar" style={{ ['--i' as string]: 2 }} />
      </g>
    </svg>
  );
}

function PaybackArt() {
  // a miniature of the calculator's strip: violet notches, then lime notches
  return (
    <div className="lpft-art lpft-strip" aria-hidden="true">
      {Array.from({ length: 14 }).map((_, i) => (
        <span key={i} className={i < 8 ? 'lpft-n lpft-nv' : 'lpft-n lpft-nl'} style={{ ['--i' as string]: i }} />
      ))}
    </div>
  );
}

function Badge({ children, tone }: { children: React.ReactNode; tone?: 'free' }) {
  return <span className={`lpft-badge${tone === 'free' ? ' lpft-badge-free' : ''}`}>{children}</span>;
}

export function FreeToolsShowcase({
  columns,
  onItemClick,
  variant = 'desktop',
  open = true,
}: {
  columns: Column[];
  onItemClick?: () => void;
  variant?: 'desktop' | 'mobile';
  open?: boolean;
}) {
  const tools = columns.find((c) => c.title === 'Free Tools')?.items ?? [];
  const guides = columns.find((c) => c.title === 'Free Guides')?.items ?? [];
  let n = 0;
  const step = () => ({ ['--s' as string]: n++ });

  return (
    <div className={`lpft lpft-${variant}`} data-open={open ? 'true' : 'false'} data-testid="free-tools-showcase">
      <style>{CSS}</style>
      <p className="lpft-label" style={step()}>Free Tools</p>
      <div className="lpft-tools">
        {tools.map((tool, i) => {
          const isGrader = tool.href === GRADER_HREF;
          return (
            <Link
              key={tool.href}
              href={tool.href}
              onClick={onItemClick}
              className={`lpft-card lpft-tool ${isGrader ? 'lpft-hero' : 'lpft-calc'}`}
              style={step()}
              data-tool={isGrader ? 'grader' : 'roi'}
            >
              <span className="lpft-edge" aria-hidden="true" />
              <span className="lpft-visual">{isGrader ? <GraderArt /> : <PaybackArt />}</span>
              <span className="lpft-copy">
                <span className="lpft-pills">
                  <Badge tone="free">Free</Badge>
                  {isGrader && <Badge>About 60 seconds</Badge>}
                </span>
                <span className="lpft-title">{tool.label}</span>
                <span className="lpft-desc">{tool.description}</span>
                <span className="lpft-go" aria-hidden="true">
                  <ArrowRight className="lpft-arrow" />
                </span>
              </span>
            </Link>
          );
        })}
      </div>

      <p className="lpft-label lpft-label-2" style={step()}>Free Guides</p>
      <div className="lpft-guides" data-n={guides.length}>
        {guides.map((g, i) => (
          <Link key={g.href} href={g.href} onClick={onItemClick} className="lpft-card lpft-guide" style={step()}>
            <span className="lpft-edge" aria-hidden="true" />
            <span className={`lpft-cover ${COVER_TINTS[i % COVER_TINTS.length]}`} aria-hidden="true">
              <span className="lpft-spine" />
              <span className="lpft-lines"><i /><i /><i /></span>
            </span>
            <span className="lpft-gcopy">
              <span className="lpft-gtitle">{g.label}</span>
              <span className="lpft-gdesc">{g.description}</span>
              {g.href === DOWNLOADED_CLAIM_HREF && <span className="lpft-dl">Downloaded 25,000+ times</span>}
            </span>
            <ArrowRight className="lpft-garrow" aria-hidden="true" />
          </Link>
        ))}
      </div>
    </div>
  );
}

// Tokens: light by default; dark on the v2 skin pages unless the light mode attribute is set (same switch the skin uses).
const CSS = `
.lpft{--ft-ink:#0b0d18;--ft-mut:#5b6076;--ft-card:#fff;--ft-line:rgba(11,13,24,.12);--ft-soft:rgba(124,92,255,.08);--ft-acc:#0a8fb0;--ft-track:rgba(11,13,24,.1);--ft-core:#f3f0ff;--ft-shadow:rgba(60,40,140,.28);color:var(--ft-ink)}
html[data-lp]:not([data-skin-mode="light"]) .lpft,html:not([data-lp]) .lpft,html[data-skin="game"]:not([data-skin-mode="light"]) .lpft{--ft-ink:#fff;--ft-mut:rgba(255,255,255,.7);--ft-card:rgba(255,255,255,.045);--ft-line:rgba(255,255,255,.14);--ft-soft:rgba(255,255,255,.07);--ft-acc:#22e4ff;--ft-track:rgba(255,255,255,.12);--ft-core:#14152b;--ft-shadow:rgba(0,0,0,.7)}
.lpft *{box-sizing:border-box}
.lpft-label{margin:0 0 .7rem;font-size:.72rem;font-weight:800;letter-spacing:.16em;text-transform:uppercase;color:var(--ft-acc)}
.lpft-label-2{margin-top:1.4rem}
.lpft-tools{display:grid;grid-template-columns:1.25fr 1fr;gap:1rem}
.lpft-guides{display:grid;grid-template-columns:repeat(3,1fr);gap:.9rem}
.lpft-guides[data-n="4"]{grid-template-columns:repeat(2,1fr)}
.lpft-card{position:relative;display:flex;border-radius:18px;border:1.5px solid var(--ft-line);background:var(--ft-card);color:var(--ft-ink);text-decoration:none;overflow:hidden;
  box-shadow:0 5px 0 var(--ft-soft);transition:transform .22s ease,opacity .35s ease,border-color .2s,box-shadow .22s;opacity:0;transform:translateY(10px);transition-delay:calc(var(--s,0)*55ms + 80ms),calc(var(--s,0)*55ms + 80ms),0s,0s}
.lpft[data-open="true"] .lpft-card,.lpft[data-open="true"] .lpft-label{opacity:1;transform:none}
.lpft-label{opacity:0;transition:opacity .35s ease;transition-delay:calc(var(--s,0)*55ms + 80ms)}
.lpft[data-open="true"] .lpft-card:hover,.lpft[data-open="true"] .lpft-card:focus-visible{transform:translateY(-4px);transition-delay:0s;border-color:var(--ft-acc);box-shadow:0 9px 0 var(--ft-soft),0 18px 40px -16px var(--ft-shadow),0 0 28px -8px rgba(124,92,255,.55)}
.lpft-card:focus-visible{outline:2px solid #7c5cff;outline-offset:2px}
.lpft-edge{position:absolute;inset:0 0 auto 0;height:3px;background:linear-gradient(90deg,#ff34a7,#7c5cff,#22e4ff);transform:scaleX(.18);transform-origin:left;transition:transform .45s ease;pointer-events:none}
.lpft-card:hover .lpft-edge,.lpft-card:focus-visible .lpft-edge{transform:scaleX(1)}
.lpft-tool{flex-direction:column;min-height:210px}
.lpft-hero{background:linear-gradient(135deg,rgba(124,92,255,.2),rgba(255,52,167,.08) 55%,rgba(34,228,255,.1)),var(--ft-card)}
.lpft-visual{display:flex;align-items:center;justify-content:center;padding:1.1rem 1rem .4rem;min-height:130px}
.lpft-art{display:block}
.lpft-grader{width:270px;max-width:100%;height:auto;overflow:visible}
.lpft-track{stroke:var(--ft-track)}.lpft-track-fill{fill:var(--ft-track)}.lpft-core{fill:var(--ft-core)}
.lpft-sweep{transform-origin:0 0;transform:rotate(-200deg);transition:transform .9s cubic-bezier(.2,.8,.2,1) .25s}
.lpft[data-open="true"] .lpft-sweep{transform:none}
.lpft-bar{transform-box:fill-box;transform-origin:left;transform:scaleX(0);transition:transform .7s cubic-bezier(.2,.8,.2,1);transition-delay:calc(.45s + var(--i,0)*90ms)}
.lpft[data-open="true"] .lpft-bar{transform:none}
.lpft-hero:hover .lpft-sweep{transform:rotate(14deg);transition-delay:0s}
.lpft-strip{display:flex;gap:5px;align-items:flex-end;width:100%;max-width:300px;height:78px;margin-top:.4rem}
.lpft-n{flex:1;height:100%;border-radius:4px;transform-origin:bottom;transform:scaleY(.25);transition:transform .5s cubic-bezier(.2,.8,.2,1);transition-delay:calc(.35s + var(--i,0)*35ms)}
.lpft[data-open="true"] .lpft-n{transform:none}
.lpft-nv{background:linear-gradient(180deg,#a58bff,#7c5cff)}
.lpft-nl{background:linear-gradient(180deg,#e2ff9a,#b6ff5c)}
.lpft-calc:hover .lpft-n{transform:scaleY(1.12)}
.lpft-copy{position:relative;display:flex;flex-direction:column;gap:.3rem;padding:.4rem 1.25rem 1.2rem;flex:1}
.lpft-pills{display:flex;flex-wrap:wrap;gap:.4rem;margin-bottom:.15rem}
.lpft-badge{display:inline-block;padding:.18rem .6rem;border-radius:999px;border:1px solid var(--ft-line);font-size:.68rem;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:var(--ft-mut);white-space:nowrap}
.lpft-badge-free{border-color:transparent;color:#fff;background:linear-gradient(95deg,#7c5cff,#a24bff)}
.lpft-title{font-family:var(--font-display),ui-sans-serif,system-ui,sans-serif;font-weight:800;font-size:1.3rem;line-height:1.15;letter-spacing:-.015em;color:var(--ft-ink)}
.lpft-desc{font-size:.9rem;line-height:1.4;color:var(--ft-mut);max-width:34ch}
.lpft-go{position:absolute;right:1.1rem;bottom:1.1rem;display:grid;place-items:center;width:2.25rem;height:2.25rem;border-radius:999px;background:var(--ft-soft);border:1px solid var(--ft-line)}
.lpft-arrow{width:1rem;height:1rem;color:var(--ft-ink);transition:transform .22s}
.lpft-card:hover .lpft-arrow,.lpft-card:focus-visible .lpft-arrow,.lpft-card:hover .lpft-garrow{transform:translateX(4px)}
.lpft-guide{align-items:center;gap:.9rem;padding:1rem 1rem 1rem 1rem;min-height:104px}
.lpft-cover{position:relative;flex:0 0 auto;width:52px;height:70px;border-radius:5px 8px 8px 5px;box-shadow:0 6px 14px -6px rgba(0,0,0,.6);overflow:hidden;transition:transform .25s}
.lpft-guide:hover .lpft-cover{transform:rotate(-4deg) translateY(-2px)}
.lpft-t1{background:linear-gradient(150deg,#ff34a7,#7c5cff)}.lpft-t2{background:linear-gradient(150deg,#7c5cff,#22e4ff)}.lpft-t3{background:linear-gradient(150deg,#22e4ff,#7c5cff 70%,#ff34a7)}.lpft-t4{background:linear-gradient(150deg,#294bfd,#7c5cff 55%,#ff34a7)}
.lpft-spine{position:absolute;inset:0 auto 0 0;width:7px;background:rgba(0,0,0,.28)}
.lpft-lines{position:absolute;left:14px;right:8px;top:14px;display:flex;flex-direction:column;gap:5px}
.lpft-lines i{display:block;height:4px;border-radius:2px;background:rgba(255,255,255,.85)}
.lpft-lines i:nth-child(2){width:75%}.lpft-lines i:nth-child(3){width:45%;opacity:.6}
.lpft-gcopy{display:flex;flex-direction:column;gap:.15rem;min-width:0;flex:1}
.lpft-gtitle{font-family:var(--font-display),ui-sans-serif,system-ui,sans-serif;font-weight:800;font-size:.98rem;line-height:1.2;color:var(--ft-ink)}
.lpft-gdesc{font-size:.8rem;line-height:1.35;color:var(--ft-mut)}
.lpft-dl{margin-top:.2rem;font-size:.7rem;font-weight:800;letter-spacing:.04em;color:var(--ft-acc)}
.lpft-garrow{flex:0 0 auto;width:1rem;height:1rem;color:var(--ft-mut);transition:transform .22s}
/* phone: stacked tool cards, compact guide rows */
.lpft-mobile .lpft-tools{grid-template-columns:1fr;gap:.8rem}
.lpft-mobile .lpft-guides{grid-template-columns:1fr;gap:.6rem}
.lpft-mobile .lpft-tool{min-height:0}
.lpft-mobile .lpft-visual{min-height:0;padding:1rem 1rem .25rem}
.lpft-mobile .lpft-grader{width:210px}
.lpft-mobile .lpft-strip{height:52px}
.lpft-mobile .lpft-guide{min-height:64px;padding:.7rem .85rem}
.lpft-mobile .lpft-cover{width:40px;height:54px}
.lpft-mobile .lpft-lines{left:11px;top:10px;gap:4px}
.lpft-mobile .lpft-title{font-size:1.15rem}
@media (prefers-reduced-motion:reduce){.lpft *{transition:none !important;animation:none !important}.lpft .lpft-card{opacity:1;transform:none}.lpft .lpft-label{opacity:1}.lpft .lpft-sweep,.lpft .lpft-bar,.lpft .lpft-n{transform:none}}
`;
